import { Button } from 'react-bootstrap';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { putPipelineLead } from 'redux/actions/leadsActions.js';
import ServiceInterestsEditor from '../../components/ServiceInterestsEditor.js';
import selectors from '../../selectors/selectors.js';
import setQuery from '../../lib/setQuery.js';
import sortByProp from '../../lib/sortByProp.js';
import useBoundForm from '../../hooks/useBoundForm.js';
import PipelineStatusEditor from './pipelineEditor/PipelineStatusEditor.js';
import BoundFormGroup from '../../components/boundForm/BoundFormGroup.js';
import confirmDialog from 'lib/confirmDialog.js';
import { deletePipelineLead } from 'redux/actions/pipelineActions.js';
import filterServicePersonsByLocationId from 'lib/filterServicePersonsByLocationId.js';

const getDefaultFormState = ({ pipelineLead, statusId, activeLeadContactsFormatted }) => {
  return {
    statusDate: statusId !== undefined ? new Date() : (pipelineLead || {}).status_date || new Date(),
    statusId: statusId || (pipelineLead || {}).status_id || '1',
    subStatusId: statusId === '1' ? '1' : !!statusId ? null : (pipelineLead || {}).sub_status_id || null,
    note: (pipelineLead || {}).note || '',
    userId: (pipelineLead || {}).user_id || null,
    stageId: (pipelineLead || {}).stage_id || '',
    leadId: (pipelineLead || {}).lead_id,
    pipelineId: (pipelineLead || {}).pipeline_id,
    serviceProviderId: (pipelineLead || {}).service_provider_id,
    serviceLocationId: (pipelineLead || {}).service_location_id,
    servicePersonId: (pipelineLead || {}).service_person_id,
    stageWasManuallyChanged: false,
    stageWasAutoChanged: false,
    ...(pipelineLead && pipelineLead.contact_id ? { contactId: pipelineLead.contact_id } : {}),
  };
};

const pipelineStatusHasChanged = (pipelineLead, state = {}) => {
  const fromDate = new Date((pipelineLead || {}).status_date || new Date()).toDateString();
  const toDate = new Date(state.statusDate || new Date()).toDateString();
  return !pipelineLead ? false : (pipelineLead.status_id || null) !== (state.statusId || null) || (pipelineLead.sub_status_id || null) !== (state.subStatusId || null) || (pipelineLead.service_location_id || null) !== (state.serviceLocationId || null) || (pipelineLead.user_id || null) !== (state.userId || null) || (pipelineLead.stage_id || null) !== (state.stageId || null) || (pipelineLead.note || '') !== (state.note || '') || fromDate !== toDate;
};

const PipelineEditor = ({ pipelineLead, onChange = () => {} }) => {
  const serviceProviderId = useSelector(selectors.serviceProviderId);
  const servicePersons = useSelector(selectors.serviceProviderServicePersons);
  const serviceProviderUsers = useSelector(selectors.serviceProviderUsers);
  const statusId = useSelector(selectors.statusId);
  const pipelines = useSelector(selectors.serviceProviderPipelines);
  const locations = useSelector((state) => state.currentServiceProvider.locations);
  const defaultWonStageId = useSelector(selectors.pipelineDefaultWonStageId);
  const canDelete = useSelector(selectors.hasPermission('pipelines.leads.delete'));
  const activeLeadContacts = useSelector(selectors.activeLeadContacts);
  const activeLeadContactsFormatted = activeLeadContacts && Array.isArray(activeLeadContacts) && activeLeadContacts.length ? activeLeadContacts.map((n) => ({ label: n.full_name, value: n.id })) : null;

  const defaultFormState = getDefaultFormState({ pipelineLead, statusId, activeLeadContactsFormatted });
  const form = useBoundForm({ initialState: defaultFormState });

  const pipelineStages = ((pipelines.find((n) => n.id === (pipelineLead || {}).pipeline_id) || {}).stages || []).sort(sortByProp('order', 'asc', (n) => parseFloat(n)));
  const userOptions = serviceProviderUsers.map((n) => ({ value: n.id, label: `${n.firstName || ''} ${n.lastName || ''}`.trim() }));
  const locationOptions = locations.map((n) => ({ value: n.id, label: n.service_location_name }));
  const pipelineStageOptions = pipelineStages.map((n) => ({ value: n.id, label: n.pipeline_stage_name }));
  const editorIsDirty = pipelineStatusHasChanged(pipelineLead, form.state);
  const showAutoChangeNotice = form.state.stageWasAutoChanged && form.state.stageId === defaultWonStageId && form.state.statusId === '2';
  const defaultWonStageName = ((pipelineStageOptions || []).find((n) => n.value === defaultWonStageId) || {}).label;
  const servicePersonOptions = servicePersons //
    .filter(filterServicePersonsByLocationId(form.state.serviceLocationId))
    .map((n) => ({ value: n.id, label: `${n.firstName || ''} ${n.lastName || ''}`.trim() }))
    .sort(sortByProp('label'));

  useEffect(() => form.setState({ ...defaultFormState }), [!!pipelineLead]);
  useEffect(() => onChange(form.state), [form.state]);

  const handleStageChange = (stageId) => {
    form.setState({
      stageId: stageId,
      stageWasManuallyChanged: true,
      stageWasAutoChanged: false,
    });
  };

  const handleContactChange = (contactId) => {
    form.setState({
      contactId: contactId,
    });
  };

  const handleStatusEditorChange = (payload) => {
    form.setState({ ...form.state, ...payload });
    if (payload.changeSource === 'statusEditorTabs') {
      if (payload.statusId === '2' && defaultWonStageId && !form.state.stageWasManuallyChanged) {
        form.setState({ stageId: defaultWonStageId, stageWasAutoChanged: form.state.stageId });
      } else if (payload.statusId !== '2' && form.state.stageWasAutoChanged) {
        form.setState({ stageId: form.state.stageWasAutoChanged, stageWasAutoChanged: false });
      }
    }
  };

  const handleCloseClick = () => {
    setQuery({ pipelineLeadId: null, statusId: null });
    form.reset(getDefaultFormState({ pipelineLead, statusId: null }));
  };

  const handleSaveClick = () => {
    putPipelineLead({ ...form.state, pipelineLeadId: (pipelineLead || {}).id });
    setQuery({ pipelineLeadId: null });
  };

  const handleDeleteClick = async () => {
    const confirm = await confirmDialog({
      message: `Delete this Pipeline Lead?`,
      confirm: 'Delete',
      cancel: 'Cancel',
    });
    if (confirm) {
      setQuery({ pipelineLeadId: null });
      deletePipelineLead({
        id: pipelineLead.id,
        serviceProviderId,
      });
    }
  };

  const fields = [
    [{ id: 'serviceLocationId', placeholder: 'Location', options: locationOptions }], //
    [{ id: 'servicePersonId', placeholder: 'Service Person', options: servicePersonOptions, hidden: servicePersons.length < 1, nullable: true, nullableLabel: 'None' }],
    [{ id: 'userId', placeholder: 'User', options: userOptions }],
    [{ id: 'stageId', placeholder: 'Stage', options: pipelineStageOptions, onChange: handleStageChange, className: 'stage-select' }],
    ...(pipelineLead && activeLeadContactsFormatted ? [[{ id: 'contactId', placeholder: 'Contact', nullable: true, nullableLabel: 'None', options: activeLeadContactsFormatted, onChange: handleContactChange }]] : []),
  ];

  if (pipelineLead) {
    return (
      <>
        <div className={`pipeline-editor`}>
          <BoundFormGroup formId={form.id} fields={fields} />
          {showAutoChangeNotice && <p className="stage-change-notification">Stage will update to {defaultWonStageName}</p>}
          <PipelineStatusEditor state={form.state} onChange={handleStatusEditorChange} />
          <ServiceInterestsEditor />
          <div className={'buttons'}>
            <div className={'left'}>
              <Button onClick={handleSaveClick} className={'btn-primary'}>
                Save
              </Button>
              <Button variant={''} onClick={handleCloseClick} className={'btn-secondary'}>
                Cancel
              </Button>
            </div>
            <div className={'right'}>
              {canDelete && (
                <Button variant={'danger'} onClick={handleDeleteClick}>
                  Delete
                </Button>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className={`pipeline-editor new`}>
      <PipelineStatusEditor state={form.state} onChange={handleStatusEditorChange} />
    </div>
  );
};

export default PipelineEditor;
